import {useState} from "react";
import {Helmet} from "react-helmet-async";
import {useParams, useSearchParams} from "react-router";

import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Last30Days} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {fromURL} from "@/components/datetime/utils.ts";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {RUMHeaderInput} from "@/lib/queries/rum.ts";
import {ALERTS_FIXED_RANGE_KEY} from "@/pages/alerting/AlertsPage.tsx";
import {RUMCheckActionBar} from "@/pages/rum/v2/details/RUMCheckActionBar.tsx";
import {RUMCheckDetailsContent} from "@/pages/rum/v2/details/RUMCheckDetailsContent.tsx";
import {RUMAggregationType} from "@/pages/rum/v2/types.ts";

export const RUMCheckDetails = () => {
  const {id} = useParams();
  const [queryParams, setQueryParams] = useSearchParams();
  const checkID = parseInt(id!);
  const [refreshSeconds, setRefreshSeconds] = useState<number | undefined>(undefined);

  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    fromKey: "start",
    toKey: "end",
    fixedRangeKey: ALERTS_FIXED_RANGE_KEY,
    withDefault: Last30Days,
  });
  const [urlPayload, setUrlPayload] = useState<Pick<RUMHeaderInput, "agg" | "url">>({
    agg: fromURL("agg", queryParams, (v) => v as RUMAggregationType, RUMAggregationType.MED),
    url: fromURL("url", queryParams, (v) => v, undefined),
  });

  const onURLChange = (url: string | null) => {
    setUrlPayload((prev) => ({...prev, url: url ?? undefined}));
    const newParams = new URLSearchParams(queryParams);
    if (!url) {
      newParams.delete("url");
    } else {
      newParams.set("url", url.toString());
    }
    setQueryParams(newParams);
  };

  const finalPayload: RUMHeaderInput = {
    ...urlPayload,
    start: dateRange.from,
    end: dateRange.to,
  };

  return (
    <ContentContainer
      title="Real User Monitoring Checks"
      actionsBar={
        <RUMCheckActionBar
          id={checkID}
          agg={urlPayload.agg}
          refreshSeconds={refreshSeconds}
          setRefresh={setRefreshSeconds}
          setAgg={(agg) => {
            setUrlPayload((prev) => ({...prev, agg}));
            if (agg) {
              const newParams = new URLSearchParams(queryParams);
              newParams.set("agg", agg);
              setQueryParams(newParams);
            }
          }}
          onURLChange={(v) => {
            onURLChange(v?.URLGroupHash ?? null);
          }}
          start={dateRange.from}
          end={dateRange.to}
          fixedRange={fixedRangeOption?.value}
          onRangeChange={handleRangeChange}
        />
      }
    >
      <Helmet>
        <title>Check Details</title>
      </Helmet>
      <UpErrorBoundary>
        <RUMCheckDetailsContent id={checkID} payload={finalPayload} refreshSeconds={refreshSeconds} />
      </UpErrorBoundary>
    </ContentContainer>
  );
};
